// src/utils/businessNativeSafeWrites.js
// Shared native-safe reads/writes for business settings.
// iOS/Capacitor uses fsClient (REST/native HTTP + manual-auth idToken).
// Web/Android keeps Firebase Web SDK behavior.
// Platform owner gets a verified Admin SDK fallback when a client read/write is denied or times out.

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import fsClient from "../data/firestoreClient";
import { isIosCapacitor } from "./nativeHttp";
import {
  callLoopOwnerBusinessAdmin,
  getLoopEffectiveAuthIdentity,
  isLoopPlatformOwnerIdentity,
} from "./platformOwner";

export function loopBusinessNow() {
  return isIosCapacitor() ? new Date().toISOString() : serverTimestamp();
}

export function loopBusinessEffectiveUser(authRef = null, userFallback = null) {
  const identity = getLoopEffectiveAuthIdentity(authRef, userFallback);
  if (!identity) return null;
  return {
    uid: identity.uid || null,
    email: identity.email || "",
    displayName: identity.displayName || "",
    idToken: identity.idToken || "",
    source: identity.source || "effective-auth",
    manualAuth: identity.manualAuth || false,
    getIdToken: identity.rawUser && typeof identity.rawUser.getIdToken === "function"
      ? identity.rawUser.getIdToken.bind(identity.rawUser)
      : undefined,
  };
}

export function loopBusinessIsPlatformOwner(authRef = null, userFallback = null) {
  return isLoopPlatformOwnerIdentity(getLoopEffectiveAuthIdentity(authRef, userFallback));
}

export function loopBusinessIsSignedIn(authRef = null, userFallback = null) {
  const u = loopBusinessEffectiveUser(authRef, userFallback);
  return Boolean(u && (u.uid || u.email));
}

export function loopBusinessUserId(authRef = null, userFallback = null) {
  const u = loopBusinessEffectiveUser(authRef, userFallback);
  return u?.uid || u?.email || null;
}

export async function loopBusinessWithTimeout(promise, timeoutMs = 22000, label = "ios_business_settings_timeout") {
  let timer = null;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(label)), timeoutMs);
      }),
    ]);
  } finally {
    try { if (timer) clearTimeout(timer); } catch (_) {}
  }
}

function canUseOwnerFallback() {
  try {
    return loopBusinessIsPlatformOwner();
  } catch (_) {
    return false;
  }
}

async function ownerFallback(action, args = {}) {
  return callLoopOwnerBusinessAdmin({ action, ...args });
}

export async function loopBusinessGetDoc(dbRef, path, opts = {}) {
  if (!dbRef || !path) throw new Error("missing_db_or_path");
  const timeoutMs = opts.timeoutMs || 22000;
  try {
    if (isIosCapacitor() && typeof fsClient.getDocByPath === "function") {
      return await loopBusinessWithTimeout(
        fsClient.getDocByPath(path, { db: dbRef }),
        timeoutMs,
        "ios_getdoc_timeout"
      );
    }
    const snap = await loopBusinessWithTimeout(
      getDoc(doc(dbRef, path)),
      timeoutMs,
      "web_getdoc_timeout"
    );
    return snap && snap.exists() ? { id: snap.id, ...(snap.data() || {}) } : null;
  } catch (error) {
    if (!canUseOwnerFallback()) throw error;
    const result = await ownerFallback("get", { db: dbRef, path });
    return result?.document || null;
  }
}

export async function loopBusinessSetDoc(dbRef, path, payload = {}, options = { merge: true }, opts = {}) {
  if (!dbRef || !path) throw new Error("missing_db_or_path");
  const timeoutMs = opts.timeoutMs || 22000;
  try {
    if (isIosCapacitor() && typeof fsClient.setDocByPath === "function") {
      return await loopBusinessWithTimeout(
        fsClient.setDocByPath(path, payload, { db: dbRef, merge: options?.merge !== false }),
        timeoutMs,
        "ios_setdoc_timeout"
      );
    }
    return await loopBusinessWithTimeout(
      setDoc(doc(dbRef, path), payload, options),
      timeoutMs,
      "web_setdoc_timeout"
    );
  } catch (error) {
    // Platform owner fallback is safe on Web, Android and iOS because
    // platformOwner.js serializes Firestore sentinels before calling Admin SDK.
    if (!canUseOwnerFallback()) throw error;
    return ownerFallback("set", {
      db: dbRef,
      path,
      payload,
      merge: options?.merge !== false,
    });
  }
}

export async function loopBusinessUpdateDoc(dbRef, path, payload = {}, opts = {}) {
  if (!dbRef || !path) throw new Error("missing_db_or_path");
  const timeoutMs = opts.timeoutMs || 22000;
  try {
    if (isIosCapacitor() && typeof fsClient.updateDocByPath === "function") {
      return await loopBusinessWithTimeout(
        fsClient.updateDocByPath(path, payload, { db: dbRef }),
        timeoutMs,
        "ios_updatedoc_timeout"
      );
    }
    return await loopBusinessWithTimeout(
      updateDoc(doc(dbRef, path), payload),
      timeoutMs,
      "web_updatedoc_timeout"
    );
  } catch (error) {
    if (!canUseOwnerFallback()) throw error;
    return ownerFallback("update", { db: dbRef, path, payload });
  }
}

export async function loopBusinessAddDoc(dbRef, collectionPath, payload = {}, opts = {}) {
  if (!dbRef || !collectionPath) throw new Error("missing_db_or_collection");
  const timeoutMs = opts.timeoutMs || 22000;
  try {
    if (isIosCapacitor() && typeof fsClient.addDocToCollectionPath === "function") {
      return await loopBusinessWithTimeout(
        fsClient.addDocToCollectionPath(collectionPath, payload, { db: dbRef }),
        timeoutMs,
        "ios_adddoc_timeout"
      );
    }
    return await loopBusinessWithTimeout(
      addDoc(collection(dbRef, collectionPath), payload),
      timeoutMs,
      "web_adddoc_timeout"
    );
  } catch (error) {
    if (!canUseOwnerFallback()) throw error;
    const result = await ownerFallback("add", { db: dbRef, collectionPath, payload });
    return { id: result?.id || result?.documentId || null, ...result };
  }
}

export async function loopBusinessDeleteDoc(dbRef, path, opts = {}) {
  if (!dbRef || !path) throw new Error("missing_db_or_path");
  const timeoutMs = opts.timeoutMs || 22000;
  try {
    if (isIosCapacitor() && typeof fsClient.deleteDocByPath === "function") {
      return await loopBusinessWithTimeout(
        fsClient.deleteDocByPath(path, { db: dbRef }),
        timeoutMs,
        "ios_deletedoc_timeout"
      );
    }
    return await loopBusinessWithTimeout(
      deleteDoc(doc(dbRef, path)),
      timeoutMs,
      "web_deletedoc_timeout"
    );
  } catch (error) {
    if (!canUseOwnerFallback()) throw error;
    return ownerFallback("delete", { db: dbRef, path });
  }
}

export async function loopBusinessListCollection(dbRef, collectionPath, opts = {}) {
  if (!dbRef || !collectionPath) throw new Error("missing_db_or_collection");
  const timeoutMs = opts.timeoutMs || 22000;
  try {
    if (isIosCapacitor() && typeof fsClient.listCollectionByPath === "function") {
      const rows = await loopBusinessWithTimeout(
        fsClient.listCollectionByPath(collectionPath, opts.query || {}, { db: dbRef }),
        timeoutMs,
        "ios_listcollection_timeout"
      );
      return Array.isArray(rows) ? rows : [];
    }
    const snap = await loopBusinessWithTimeout(
      getDocs(collection(dbRef, collectionPath)),
      timeoutMs,
      "web_listcollection_timeout"
    );
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
  } catch (error) {
    if (!canUseOwnerFallback()) throw error;
    const result = await ownerFallback("list", {
      db: dbRef,
      collectionPath,
      limit: opts?.query?.limit || opts?.limit || 1000,
    });
    return Array.isArray(result?.documents) ? result.documents : [];
  }
}

export async function loopBusinessBulkUpdateCollection(dbRef, collectionPath, makePatch, opts = {}) {
  const rows = await loopBusinessListCollection(dbRef, collectionPath, opts);
  const validRows = rows.filter((row) => row?.id);
  if (!validRows.length) return [];

  if (isIosCapacitor()) {
    await Promise.all(validRows.map((row, idx) => {
      const patch = typeof makePatch === "function" ? makePatch(row, idx) : makePatch;
      if (!patch || typeof patch !== "object") return Promise.resolve();
      return loopBusinessUpdateDoc(dbRef, `${collectionPath}/${row.id}`, patch, opts);
    }));
    return validRows;
  }

  const batch = writeBatch(dbRef);
  validRows.forEach((row, idx) => {
    const patch = typeof makePatch === "function" ? makePatch(row, idx) : makePatch;
    if (!patch || typeof patch !== "object") return;
    batch.update(doc(dbRef, `${collectionPath}/${row.id}`), patch);
  });
  try {
    await loopBusinessWithTimeout(batch.commit(), opts.timeoutMs || 22000, "web_batch_timeout");
  } catch (error) {
    if (!canUseOwnerFallback()) throw error;
    await Promise.all(validRows.map((row, idx) => {
      const patch = typeof makePatch === "function" ? makePatch(row, idx) : makePatch;
      if (!patch || typeof patch !== "object") return Promise.resolve();
      return ownerFallback("update", { db: dbRef, path: `${collectionPath}/${row.id}`, payload: patch });
    }));
  }
  return validRows;
}
